import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Camera, LogIn, LogOut, MapPin, ShieldCheck, X, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import LogoutButton from "@/components/LogoutButton";

import { useTranslation } from "react-i18next";
/**
 * Manager Attendance — STRICT selfie + GPS check-in for MANAGER and DEALER
 * roles, mirroring the CSA flow. Reuses /attendance/check-in and
 * /attendance/check-out endpoints. History shows the last 30 attendances so
 * managers can prove they were on-site during audits.
 */
export default function ManagerAttendance() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const nav = useNavigate();
  const [shifts, setShifts] = useState([]);
  const [shiftId, setShiftId] = useState("");
  const [pump, setPump] = useState(null);
  const [attendanceMode, setAttendanceMode] = useState("STRICT");
  const [active, setActive] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sh, ac, hi] = await Promise.all([
        api.get("/shifts").catch(() => ({ data: {} })),
        api.get("/attendance/active").catch(() => ({ data: {} })),
        api.get("/attendance/mine?limit=30").catch(() => ({ data: { items: [] } })),
      ]);
      setShifts(sh.data.shifts || []);
      setPump(sh.data.pump || null);
      setAttendanceMode((sh.data.pump?.attendance_mode || "STRICT").toUpperCase());
      if ((sh.data.shifts || []).length) setShiftId(sh.data.shifts[0].id);
      setActive(ac.data.attendance || null);
      setHistory(hi.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="manager-attendance-page">
      <header className="sticky top-0 z-40 border-b border-border bg-card/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-2 px-4 py-3">
          <button className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" onClick={() => nav("/")} data-testid="mgr-att-back">
            <ArrowLeft className="h-4 w-4" /> Back
          </button>
          <div className="flex items-center gap-2 text-sm font-semibold">
            <ShieldCheck className="h-4 w-4 text-primary" /> My Attendance
          </div>
          <LogoutButton where="manager-attendance" />
        </div>
      </header>

      <main className="mx-auto max-w-3xl space-y-5 px-4 py-6 md:px-6">
        {loading ? (
          <div className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">{t("common.loading")}</div>
        ) : active ? (
          <ActiveCard attendance={active} onCheckedOut={load} />
        ) : (
          <CheckInCard
            shifts={shifts}
            shiftId={shiftId}
            setShiftId={setShiftId}
            pump={pump}
            attendanceMode={attendanceMode}
            onCheckedIn={load}
          />
        )}

        <HistoryCard history={history} you={user} />
      </main>
    </div>
  );
}

function ActiveCard({ attendance, onCheckedOut }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const doCheckOut = async () => {
    if (!window.confirm("End your on-site shift now?")) return;
    setBusy(true);
    try {
      await api.post("/attendance/check-out");
      toast.success(t("manager.myattendance.checked_out_see_you_tomorrow"));
      onCheckedOut();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const inAt = new Date(attendance.checked_in_at);
  const mins = Math.max(0, Math.floor((Date.now() - inAt.getTime()) / 60000));
  return (
    <Card className="border-2 border-emerald-400/60 bg-emerald-50/40 dark:bg-emerald-950/20" data-testid="mgr-att-active-card">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-lg">
          <CheckCircle2 className="h-5 w-5 text-emerald-600" />
          You're on-site
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 text-sm md:grid-cols-3">
          <div><span className="text-muted-foreground">Shift · </span><b>{attendance.shift_name}</b></div>
          <div><span className="text-muted-foreground">Since · </span><b>{inAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</b></div>
          <div><span className="text-muted-foreground">Elapsed · </span><b>{Math.floor(mins / 60)}h {mins % 60}m</b></div>
        </div>
        {attendance.distance_m != null && (
          <div className="text-xs text-muted-foreground">
            <MapPin className="mr-1 inline h-3.5 w-3.5" /> ~{Math.round(attendance.distance_m)}m from pump when you checked in
          </div>
        )}
        <Button variant="destructive" onClick={doCheckOut} disabled={busy} data-testid="mgr-att-checkout" className="w-full">
          <LogOut className="h-4 w-4" /> {busy ? "Ending…" : "End on-site shift"}
        </Button>
      </CardContent>
    </Card>
  );
}

function CheckInCard({ shifts, shiftId, setShiftId, pump, attendanceMode, onCheckedIn }) {
  const { t } = useTranslation();
  const [coords, setCoords] = useState(null);
  const [locating, setLocating] = useState(false);
  const [selfie, setSelfie] = useState(null);
  const [camOpen, setCamOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const attendanceOff = attendanceMode === "OFF";

  const getLocation = () => {
    setLocating(true);
    if (!navigator.geolocation) { toast.error(t("manager.myattendance.geolocation_not_supported")); setLocating(false); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }); setLocating(false); },
      (err) => { toast.error(`Location error: ${err.message}`); setLocating(false); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
    );
  };

  const submit = async () => {
    if (!shiftId) return toast.error(t("csa.csaportal.pick_a_shift"));
    if (!attendanceOff && !coords) return toast.error(t("csa.csaportal.capture_your_location_first"));
    if (!attendanceOff && !selfie) return toast.error(t("csa.csaportal.take_a_selfie_first"));
    setSubmitting(true);
    try {
      const payload = { shift_id: shiftId };
      if (!attendanceOff) {
        payload.latitude = coords.lat;
        payload.longitude = coords.lng;
        payload.selfie_base64 = selfie;
      }
      await api.post("/attendance/check-in", payload);
      toast.success(t("manager.myattendance.checked_in"));
      onCheckedIn();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const dist = coords && pump?.latitude
    ? Math.round(haversine(coords.lat, coords.lng, pump.latitude, pump.longitude))
    : null;

  return (
    <Card className="border-border" data-testid="mgr-att-checkin-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          <LogIn className="h-5 w-5 text-amber-500" /> Start your on-site shift
        </CardTitle>
        <p className="text-xs text-muted-foreground">Same STRICT selfie + GPS as salesmen. This proves you were on-site during the shift.</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("csa.shift")}</Label>
          {shifts.length === 0 ? (
            <div className="mt-1 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-800 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
              No shifts configured. Set up shifts in Pump Setup first.
            </div>
          ) : (
            <Select value={shiftId} onValueChange={setShiftId}>
              <SelectTrigger data-testid="mgr-att-shift" className="mt-1 h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {shifts.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name} · {s.start_time || ""}–{s.end_time || ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {attendanceOff ? (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50/70 p-3 text-xs text-emerald-900 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-200">
            Your dealer has disabled the STRICT selfie + GPS. Tap Start to log attendance.
          </div>
        ) : (
          <>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-muted-foreground">{t("setup.location")}</span>
                  <Button size="sm" variant="outline" onClick={getLocation} disabled={locating} data-testid="mgr-att-loc">
                    <MapPin className="h-3.5 w-3.5" /> {locating ? "…" : coords ? "Refresh" : "Capture"}
                  </Button>
                </div>
                {coords ? (
                  <div className="mt-2 space-y-0.5 text-[11px] font-mono">
                    <div>{t("csa.csaportal.lat")} <b>{coords.lat.toFixed(6)}</b></div>
                    <div>{t("csa.csaportal.lng")} <b>{coords.lng.toFixed(6)}</b></div>
                    {dist != null && <div className="text-muted-foreground">~{dist}m from pump</div>}
                  </div>
                ) : (
                  <div className="mt-2 text-[11px] text-muted-foreground">{t("manager.myattendance.tap_capture_to_get_your_gps")}</div>
                )}
              </div>

              <div className="rounded-xl border border-border bg-card p-3">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-semibold text-muted-foreground">{t("manager.myattendance.selfie")}</span>
                  <Button size="sm" variant="outline" onClick={() => setCamOpen(true)} data-testid="mgr-att-cam">
                    <Camera className="h-3.5 w-3.5" /> {selfie ? "Retake" : "Take"}
                  </Button>
                </div>
                {selfie ? (
                  <img src={selfie} alt="selfie" className="mx-auto mt-2 h-24 w-24 rounded-lg object-cover shadow-sm" />
                ) : (
                  <div className="mt-2 text-[11px] text-muted-foreground">{t("manager.myattendance.tap_take_to_verify_your_face")}</div>
                )}
              </div>
            </div>
          </>
        )}

        <Button
          className="h-12 w-full"
          onClick={submit}
          disabled={submitting || !shiftId || (!attendanceOff && (!coords || !selfie))}
          data-testid="mgr-att-submit"
        >
          <LogIn className="h-4 w-4" /> {submitting ? "Starting…" : "Start on-site shift"}
        </Button>

        {camOpen && <SelfieDialog onClose={() => setCamOpen(false)} onCapture={(d) => { setSelfie(d); setCamOpen(false); }} />}
      </CardContent>
    </Card>
  );
}

function SelfieDialog({ onClose, onCapture }) {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 640 } }, audio: false });
        if (cancelled) { s.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) videoRef.current.srcObject = s;
      } catch { toast.error(t("manager.myattendance.camera_permission_denied")); }
    })();
    return () => { cancelled = true; streamRef.current?.getTracks().forEach((t) => t.stop()); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const capture = () => {
    const v = videoRef.current;
    if (!v) return;
    const c = document.createElement("canvas");
    c.width = v.videoWidth || 480; c.height = v.videoHeight || 640;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    onCapture(c.toDataURL("image/jpeg", 0.75));
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="mgr-att-selfie-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Take selfie
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          </DialogTitle>
        </DialogHeader>
        <video ref={videoRef} autoPlay playsInline muted className="h-64 w-full rounded-lg bg-black object-cover" />
        <Button onClick={capture} className="mt-2 w-full" data-testid="mgr-att-selfie-capture">
          <Camera className="h-4 w-4" /> Capture
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function HistoryCard({ history, you }) {
  if (!history.length) return null;
  return (
    <Card className="border-border" data-testid="mgr-att-history">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-muted-foreground" /> Recent attendance
        </CardTitle>
        <p className="text-xs text-muted-foreground">Last {history.length} check-in{history.length === 1 ? "" : "s"} for {you?.name}. Read-only audit trail.</p>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-border text-sm">
          {history.map((a) => {
            const inAt = a.checked_in_at ? new Date(a.checked_in_at) : null;
            const outAt = a.checked_out_at ? new Date(a.checked_out_at) : null;
            const mins = inAt && outAt ? Math.floor((outAt - inAt) / 60000) : null;
            return (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-xs" data-testid={`mgr-att-row-${a.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold">{a.date} · {a.shift_name}</div>
                  <div className="mt-0.5 text-[11px] text-muted-foreground">
                    In {inAt ? inAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "—"}
                    {outAt ? ` · Out ${outAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : " · still on-site"}
                    {mins != null && ` · ${Math.floor(mins/60)}h ${mins % 60}m`}
                  </div>
                </div>
                {a.distance_m != null && (
                  <span className="rounded-full bg-secondary px-2 py-0.5 font-mono text-[10px]">~{Math.round(a.distance_m)}m</span>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function haversine(lat1, lon1, lat2, lon2) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}
